import React, { useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { contactInfo, stats } from '../mock';

const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('revealed');
            }, index * 100);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen py-20" style={{ backgroundColor: 'var(--bg-1)' }}>
      {/* Hero Section */}
      <section className="mb-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-dark mb-6">
              About DVV Projects
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Delivering Value & Vision in Visakhapatnam's Real Estate Sector
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="mb-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-reveal">
  <img
  src="/office-1.png"
  alt="DVV Projects Office"
  className="rounded-xl shadow-2xl w-full h-96 object-cover"
/>



            </div>
            <div className="scroll-reveal">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-6">
                Who We Are
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                DVV Projects is a Visakhapatnam-based property investment company specializing in VMRDA- and RERA-approved residential plot layouts, villas, flats and resort-style properties — delivering clear titles, ready registration and timely development.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                With over 15 years of excellence in the real estate sector, we have successfully completed more than 12 projects and helped over 450 families realize their dream of owning property in Visakhapatnam.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our commitment to transparency, legal compliance, and customer satisfaction has made us one of the most trusted names in Visakhapatnam's real estate market. Every project we undertake carries the assurance of proper approvals, clear documentation, and quality development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-brand-dark text-white mb-20">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center scroll-reveal">
                <div className="text-4xl md:text-5xl font-bold text-brand-gold mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="mb-20">
        <div className="container-custom">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Transparency',
                description: 'We believe in complete transparency in all our dealings. Every document, every approval, every detail is shared with our customers openly.'
              },
              {
                title: 'Legal Compliance',
                description: 'All our projects carry genuine VMRDA and RERA approvals. We never compromise on legal compliance and due process.'
              },
              {
                title: 'Customer First',
                description: 'Our customers\' satisfaction is our success. We go the extra mile to ensure smooth, hassle-free property registration and ownership transfer.'
              }
            ].map((value, index) => (
              <Card key={index} className="scroll-reveal">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="w-3 h-3 bg-brand-gold rounded-full" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Office Location */}
      <section className="mb-20">
  <div className="container-custom">

    <div className="text-center mb-12 scroll-reveal">
      <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-4">
        Visit Our Offices
      </h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Visit either of our offices to discuss your property requirements.
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

      {/* Head Office */}
      <Card className="overflow-hidden scroll-reveal">
        <CardContent className="p-0">

          <div className="p-6">
            <h3 className="text-2xl font-bold text-brand-dark mb-4">
              Head Office
            </h3>

            <div className="flex items-start gap-3 mb-4">
              <MapPin size={20} className="text-brand-gold flex-shrink-0 mt-1" />
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {contactInfo.addresses.headOffice}
              </p>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <Phone size={18} className="text-brand-gold" />
              <a
                href={`tel:${contactInfo.phone}`}
                className="text-brand-gold hover:underline"
              >
                {contactInfo.phone}
              </a>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <Mail size={18} className="text-brand-gold" />
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-brand-gold hover:underline"
              >
                {contactInfo.email}
              </a>
            </div>

            <div className="flex items-center gap-3">
              <Clock size={18} className="text-brand-gold" />
              <span className="text-gray-600">
                {contactInfo.businessHours}
              </span>
            </div>
          </div>

          <iframe
            src={contactInfo.maps.headOffice}
            width="100%"
            height="300"
            style={{ border: 0 }}
            loading="lazy"
            title="Head Office"
          />

        </CardContent>
      </Card>

      {/* Branch Office */}
      <Card className="overflow-hidden scroll-reveal">
        <CardContent className="p-0">

          <div className="p-6">
            <h3 className="text-2xl font-bold text-brand-dark mb-4">
              Branch Office
            </h3>

            <div className="flex items-start gap-3 mb-4">
              <MapPin size={20} className="text-brand-gold flex-shrink-0 mt-1" />
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                {contactInfo.addresses.branchOffice}
              </p>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <Phone size={18} className="text-brand-gold" />
              <a
                href={`tel:${contactInfo.phone}`}
                className="text-brand-gold hover:underline"
              >
                {contactInfo.phone}
              </a>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <Mail size={18} className="text-brand-gold" />
              <a
                href={`mailto:${contactInfo.email}`}
                className="text-brand-gold hover:underline"
              >
                {contactInfo.email}
              </a>
            </div>

            <div className="flex items-center gap-3">
              <Clock size={18} className="text-brand-gold" />
              <span className="text-gray-600">
                {contactInfo.businessHours}
              </span>
            </div>
          </div>

          <iframe
            src={contactInfo.maps.branchOffice}
            width="100%"
            height="300"
            style={{ border: 0 }}
            loading="lazy"
            title="Branch Office"
          />

        </CardContent>
      </Card>

    </div>
  </div>
</section>
    </div>
  );
};

export default About;
